import {classNames} from '@shopify/css-utilities';
import type {RefCallback} from 'preact';
import {forwardRef} from 'preact/compat';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';

import {Modal} from '~/components/Modal';
import {useAuthorizeState} from '~/foundation/AuthorizeState/hooks';
import {useBugsnag} from '~/foundation/Bugsnag/hooks';
import {useRootProvider} from '~/foundation/RootProvider/hooks';
import {useAuthorizeEventListener} from '~/hooks/useAuthorizeEventListener';
import {useLoadTimeout} from '~/hooks/useLoadTimeout';
import type {
  ShopModalHiddenDismissMethod,
  ShopModalShownReason,
} from '~/types/analytics';
import type {SdkErrorCode} from '~/types/authorize';
import type {PayEvents} from '~/types/event';
import type {IframeElement, IframeElementCloseParams} from '~/types/iframe';
import {exchangeLoginCookie} from '~/utils/cookieExchange';
import {postMessage} from '~/utils/postMessage';
import {updateIframeSrc} from '~/utils/updateIframeSrc';

import {useLeadCaptureMonorail} from '../monorail';
import type {
  AuthenticateCallback,
  CompleteCallback,
  ConfirmSuccessCallback,
  ErrorCallback,
  LeadCaptureProps,
  LoadCallback,
  RestartCallback,
} from '../types';
import {buildDiscountAuthorizeUrl} from '../utils/buildDiscountAuthorizeUrl';

export type IframeProps = Pick<
  LeadCaptureProps,
  'apiKey' | 'devMode' | 'phoneCapture' | 'storefrontOrigin'
> & {
  onAuthenticate?: AuthenticateCallback;
  onComplete?: CompleteCallback;
  onConfirmSuccess?: ConfirmSuccessCallback;
  onError?: ErrorCallback;
  onLoad?: LoadCallback;
  onRestart?: RestartCallback;
  phoneCaptureDisclosureText?: string;
};

export const Iframe = forwardRef<IframeElement, IframeProps>(
  (
    {
      apiKey,
      devMode,
      onAuthenticate,
      onComplete,
      onConfirmSuccess,
      onError,
      onLoad,
      onRestart,
      phoneCapture,
      phoneCaptureDisclosureText,
      storefrontOrigin,
    },
    ref,
  ) => {
    const {dispatch, loaded, modalVisible} = useAuthorizeState();
    const {leaveBreadcrumb, notify} = useBugsnag();
    const {
      setShopAccountUuid,
      trackDiscountStatus,
      trackError,
      trackFirstTimeRender,
      trackLeadCapturePageImpression,
      trackModalStateChange,
    } = useLeadCaptureMonorail();
    const {instanceId} = useRootProvider();
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const {clearLoadTimeout, initLoadTimeout} = useLoadTimeout();
    const [loadedCallbackHasRun, setLoadedCallbackHasRun] = useState(false);
    const [userRestarted, setUserRestarted] = useState(false);

    const callbackRef: RefCallback<HTMLIFrameElement> = (ref) => {
      /**
       * When the user restarts, the render logic changes for our iframe and ref is set to null.
       * We want to prevent from overriding the ref and setting it to null here because it breaks
       * the postMessage logic.
       */
      if (!ref) {
        return;
      }

      iframeRef.current = ref;
    };

    useEffect(() => {
      if (loaded) {
        trackModalStateChange({
          currentState: 'loaded',
          reason: 'event_loaded',
        });
        trackFirstTimeRender();
      }
    }, [loaded, trackFirstTimeRender, trackModalStateChange]);

    const handleShowModal = useCallback(
      (reason: ShopModalShownReason) => {
        dispatch({type: 'setModalVisible', payload: true});
        trackModalStateChange({
          currentState: 'shown',
          reason,
        });
      },
      [dispatch, trackModalStateChange],
    );

    const postMessageCallback = useCallback(
      (event: PayEvents) =>
        postMessage({
          contentWindow: iframeRef?.current?.contentWindow,
          event,
        }),
      [],
    );

    const src = useMemo(() => {
      return buildDiscountAuthorizeUrl({
        analyticsTraceId: instanceId,
        apiKey,
        devMode,
        flowVersion: phoneCapture ? 'PHONE_CAPTURE' : 'EMAIL_CAPTURE',
        phoneCapture,
        requireVerification: userRestarted,
        storefrontOrigin,
      });
    }, [
      apiKey,
      devMode,
      instanceId,
      phoneCapture,
      storefrontOrigin,
      userRestarted,
    ]);

    const reloadIframe = useCallback(() => {
      updateIframeSrc({iframe: iframeRef.current, src});
    }, [src]);

    const handleError = useCallback(
      (code: SdkErrorCode, message: string, email?: string) => {
        trackError(code, message);
        onError?.({code, email: email || '', message});
      },
      [onError, trackError],
    );

    const getSideEffects = useCallback(
      async (email?: string) => {
        if (!onAuthenticate) {
          // eslint-disable-next-line no-console
          console.error(
            'No on-authenticate callback found. See the dev docs for more information on how to offer a discount code: https://shopify.dev/docs/apps/build/shop/shop-identity-sdk/components/lead-capture/offer-a-discount-code',
          );
          return;
        }

        try {
          if (devMode && email === 'discount-code-error@myshopify.io') {
            throw new Error('Discount code error');
          }

          const {discount} = await onAuthenticate();
          const {code, gid} = discount;

          if (typeof code !== 'string' || code.trim().length === 0) {
            throw new Error('discount code is non-string or empty');
          }

          if (!gid || typeof gid !== 'string') {
            // In the future we will begin requiring gid. Put a warning in the console for now.
            // eslint-disable-next-line no-console
            console.warn?.(
              '⚠️ Shop Lead Capture: discount code callback did not return a value for gid. It is strongly recommended to provide a gid for the discount code. This will be required in the future.',
            );
          }

          trackDiscountStatus(discount.code);

          if (phoneCapture) {
            postMessageCallback({
              disclosureText: phoneCaptureDisclosureText || '',
              type: 'phonecapturedisclosure',
            });
          }
          postMessageCallback({
            discountCode: code,
            gid,
            type: 'savediscount',
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Unknown error in getSideEffects callback.';
          handleError('no_discount_received', errorMessage);
        }
      },
      [
        devMode,
        handleError,
        onAuthenticate,
        phoneCapture,
        phoneCaptureDisclosureText,
        postMessageCallback,
        trackDiscountStatus,
      ],
    );

    const {destroy, waitForMessage} = useAuthorizeEventListener({
      includeCore: true,
      onClose: () => {},
      onComplete: async (event) => {
        const {
          customerAccessToken,
          customerAccessTokenExpiresAt,
          email,
          loggedIn,
          shopAccountUuid,
          shouldFinalizeLogin,
        } = event;

        if (loggedIn && shouldFinalizeLogin) {
          await exchangeLoginCookie(storefrontOrigin, notify);
          /**
           * TODO: In the future we will publish an event to shop hub to create a user session.
           *
           * Issue: https://github.com/Shopify/shop-identity/issues/2859
           * Pull request: https://github.com/Shopify/shop-js/pull/2363
           */
        }

        handleDismissModal({
          dismissMethod: 'auto',
          reason: 'event_completed',
        });
        onComplete?.({
          customerAccessToken,
          customerAccessTokenExpiresAt,
          email: email || '',
          signedIn: loggedIn,
        });
        if (shopAccountUuid) {
          setShopAccountUuid(shopAccountUuid);
        }
      },
      onConfirmSuccess: () => {
        onConfirmSuccess?.();
      },
      onDiscountSaved: () => {
        trackLeadCapturePageImpression({
          page: 'DISCOUNT_SAVE_CONFIRMATION_PAGE',
        });
      },
      onError: ({code, email, message}) => {
        clearLoadTimeout();
        handleError(code, message, email);
      },
      onLeadCaptureLoaded: ({userFound}) => {
        dispatch({
          type: 'updateState',
          payload: {
            loaded: true,
            sessionDetected: userFound,
          },
        });

        if (loadedCallbackHasRun) {
          return;
        }

        setLoadedCallbackHasRun(true);
        clearLoadTimeout();
        onLoad?.({userFound});
        trackModalStateChange({
          currentState: 'loaded',
          reason: 'event_loaded',
        });
        trackFirstTimeRender();
      },
      onResizeIframe: ({height, width}) => {
        if (iframeRef.current) {
          iframeRef.current.style.height = `${height}px`;
          iframeRef.current.style.width = `${width}px`;
        }
      },
      onRestarted: () => {
        handleDismissModal({dismissMethod: 'auto', reason: 'event_restarted'});
        setUserRestarted(true);
        onRestart?.();
      },
      onShopUserMatched: () => {
        onAuthenticate?.();
      },
      onUserVerified: ({email}) => getSideEffects(email),
      source: iframeRef,
    });

    const handleDismissModal = useCallback(
      ({dismissMethod, reason}: IframeElementCloseParams) => {
        if (!modalVisible) {
          return;
        }

        dispatch({type: 'setModalVisible', payload: false});

        trackModalStateChange({
          currentState: 'hidden',
          dismissMethod,
          reason,
        });
      },
      [dispatch, modalVisible, trackModalStateChange],
    );

    useEffect(() => {
      return () => {
        if (iframeRef.current) {
          destroy();
        }
      };
    }, [destroy]);

    useEffect(() => {
      dispatch({type: 'setLoaded', payload: false});
    }, [dispatch, src]);

    useImperativeHandle(ref, () => {
      return {
        close: handleDismissModal,
        open: handleShowModal,
        postMessage: postMessageCallback,
        reload: reloadIframe,
        source: iframeRef.current?.contentWindow || null,
        waitForMessage,
      };
    }, [
      handleDismissModal,
      handleShowModal,
      postMessageCallback,
      reloadIframe,
      waitForMessage,
    ]);

    useEffect(() => {
      initLoadTimeout();
      leaveBreadcrumb('Iframe url updated', {src}, 'state');
    }, [src, initLoadTimeout, leaveBreadcrumb]);

    const className = classNames('m-auto border-none', 'w-full');

    const iframe = (
      <iframe
        allow="publickey-credentials-get *"
        className={className}
        data-testid="lead-capture-iframe"
        ref={callbackRef}
        src={src}
        tabIndex={0}
      />
    );

    return (
      <Modal
        onDismiss={(dismissMethod: ShopModalHiddenDismissMethod) => {
          setUserRestarted(true);
          handleDismissModal({
            dismissMethod,
            reason: 'user_dismissed',
          });
          onComplete?.({email: '', signedIn: false});
        }}
        visible={modalVisible}
        variant="leadCapture"
      >
        {iframe}
      </Modal>
    );
  },
);

Iframe.displayName = 'Iframe';
